import { keyframes } from '@emotion/react'
import { omit } from 'lodash'
import * as React from 'react'
import {
  chakra,
  Box,
  ButtonProps as ChakraButtonProps,
  Button as ChakraButton,
  Checkbox as ChakraCheckbox,
  CheckboxProps,
  FormControl as ChakraFormControl,
  FormControlProps as ChakraFormControlProps,
  FormErrorMessage as ChakraFormErrorMessage,
  FormErrorMessageProps,
  forwardRef,
  Heading as ChakraHeading,
  HeadingProps,
  Icon,
  IconButton as ChakraIconButton,
  IconButtonProps,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  Radio as ChakraRadio,
  RadioProps as ChakraRadioProps,
  Select as ChakraSelect,
  SelectProps,
  Skeleton as ChakraSkeleton,
  Switch as ChakraSwitch,
  SwitchProps,
  Text as ChakraText,
  TextProps as ChakraTextProps,
  Textarea as ChakraTextarea,
  TextareaProps as ChakraTextareaProps,
  Tooltip,
  TooltipProps,
  useMultiStyleConfig,
  SkeletonProps,
  useFormControlProps
} from '@chakra-ui/react'
import { CaretDown, Check, Minus, WarningO } from '~/icons'
import {
  ExtendedFormControlContextProps,
  FormControlExtendedContextProvider,
  useFormControlExtendedContext
} from './components/form_controls/FormControlExtendedContext'
import { useFormAlertRegistrationRef } from './components/form_alert_list/FormAlertListContext'
import { mergeRefs } from '~/common/reactUtils'
import { asLink } from './components/links/Links'

/* Chakra Components */
export * from '@chakra-ui/react'

// Rename Chakra's Card to ChakraCard to avoid naming conflicts with our own Card component
export {
  Card as ChakraCard,
  CardBody as ChakraCardBody,
  CardHeader as ChakraCardHeader,
  CardFooter as ChakraCardFooter
} from '@chakra-ui/react'
export type {
  CardProps as ChakraCardProps,
  CardBodyProps as ChakraCardBodyProps,
  CardHeaderProps as ChakraCardHeaderProps,
  CardFooterProps as ChakraCardFooterProps
} from '@chakra-ui/react'

/* Customized Chakra Components */

/**
 * VNDLY Input
 *
 * Input that allows users enter single valued data.
 *
 * Extended Chakra's props with `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/input
 */
export const Input = forwardRef<ChakraInputProps & ExtendedFormControlContextProps, 'input'>((props, ref) => {
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraInput ref={ref} {...props} {...getInputProps?.(props)} />
})

Input.displayName = 'Input'

// Input.id is used internally by Chakra when InputGroup clones the child Input
// without it, the styles are not cloned properly.
Input.id = 'Input'

/**
 * Text is the used to render text and paragraphs within an interface
 *
 * This override is only here to fix a typescript bug in Chakra 2.0.2.
 * It should be removed when we upgrade Chakra to see if the problem goes away
 *
 * TODO: Fixed isTruncated here because of a type bug in Chakra
 * See: https://v2.chakra-ui.com/docs/components/text
 */
export const Text = forwardRef<Omit<ChakraTextProps, 'children'> & { children?: any; isTruncated?: boolean }, 'text'>(
  ({ isTruncated, ...props }, ref) => {
    return (
      <ChakraText
        ref={ref}
        noOfLines={isTruncated ? 1 : undefined}
        wordBreak={isTruncated ? 'break-all' : 'normal'}
        {...props}
      />
    )
  }
)

Text.displayName = 'Text'

/**
 * VNDLY Textarea
 *
 * Textarea is used to enter an amount of text that's longer than a single line
 * Extended Chakra's props with `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/textarea
 */
export const Textarea = forwardRef<ChakraTextareaProps & ExtendedFormControlContextProps, 'textarea'>((props, ref) => {
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraTextarea ref={ref} {...props} {...getInputProps?.(props)} />
})

Textarea.displayName = 'Textarea'

const CustomCheckboxIcon = (props: any) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isIndeterminate, isChecked, ...rest } = props

  const icon = isIndeterminate ? Minus : Check
  return <Icon as={icon} w={6} h={6} opacity={Number(isChecked)} {...rest} />
}

/**
 * Checkbox
 *
 * React component used in forms when a user needs to select
 * multiple values from several options.
 *
 * Extended with custom icon and `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/checkbox
 */
export const Checkbox = React.memo(
  forwardRef<CheckboxProps & ExtendedFormControlContextProps, 'input'>(({ ...props }, ref) => {
    const { getInputProps } = useFormControlExtendedContext()
    return (
      <ChakraCheckbox data-checkbox icon={<CustomCheckboxIcon />} ref={ref} {...props} {...getInputProps?.(props)} />
    )
  })
)

Checkbox.displayName = 'CheckboxFoo'

/**
 * VNDLY Radio
 *
 * Component is used in forms when a user needs to select a single value from
 * several options.
 *
 * Extended with `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/radio
 */
export const Radio = forwardRef<ChakraRadioProps & ExtendedFormControlContextProps, 'input'>((props, ref) => {
  const { 'aria-describedby': ariaDescribedBy } = useFormControlProps(props)
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraRadio ref={ref} {...props} {...getInputProps?.({ ...props, 'aria-describedby': ariaDescribedBy })} />
})

Radio.displayName = 'Radio'

/**
 * VNDLY Select Dropdown
 *
 * Using the default Chakra Select with custom icon. This component should be used instead of Chakra's Select
 * for dropdown inputs.
 * See http://storybook.beta.vndly.com/?path=/docs/foundations-forms-selectors--select for
 * examples and usage.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/select
 */
export const Select = forwardRef<SelectProps, 'select'>((props, ref) => {
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraSelect icon={<CaretDown />} ref={ref} {...props} {...getInputProps?.(props)} />
})

Select.displayName = 'Select'

/**
 * VNDLY Switch Dropdown
 *
 * Extended Chakra's Switch with `isWarned` prop to show warning message.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/switch
 */
export const Switch = forwardRef<SwitchProps & ExtendedFormControlContextProps, 'input'>((props, ref) => {
  const { getInputProps } = useFormControlExtendedContext()
  return <ChakraSwitch ref={ref} {...props} {...getInputProps?.(props)} />
})

Switch.displayName = 'Switch'

/**
 * Form Error Message
 *
 * Error messages help users diagnose and fix problems with a users input.  Error states should not
 * be activated until after a user has interacted with the form control.
 *
 * Meant to be used in conjunction with the `isInvalid` prop on the `FormControl` component.
 *
 * Using the default Chakra FormErrorMessage with custom icon and styling. This component should be used instead
 * of Chakra's FormErrorMessage for inputs.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/form-control
 */
export const FormErrorMessage = forwardRef(
  // TODO: We are redefining children here because of a type bug in formik
  ({ children, ...props }: Omit<FormErrorMessageProps, 'textStyle' | 'children'> & { children?: any }, ref) => {
    const styles = useMultiStyleConfig('FormError', props)
    return (
      <ChakraFormErrorMessage {...props} ref={ref}>
        <WarningO __css={styles?.icon} aria-label="Form error message icon" />
        <chakra.span className="chakra-form__error-message-text">{children}</chakra.span>
      </ChakraFormErrorMessage>
    )
  }
)

FormErrorMessage.displayName = 'FormErrorMessage'

export type ExtendedFormControlProps = ChakraFormControlProps & ExtendedFormControlContextProps

/**
 * Overrides Chakra's FormControl component to provide additional context for form alerts.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/form-control
 */
export const FormControl = forwardRef<ExtendedFormControlProps, 'div'>(({ children, ...props }, ref) => {
  // If the field has an id, it will be registered with the form alert list
  const registerAlertRef = useFormAlertRegistrationRef(props)

  return (
    <ChakraFormControl ref={mergeRefs(registerAlertRef, ref)} {...omit(props, 'isWarned')}>
      <FormControlExtendedContextProvider {...props}>{children}</FormControlExtendedContextProvider>
    </ChakraFormControl>
  )
})

FormControl.displayName = 'FormControl'

export type ButtonProps = Omit<ChakraButtonProps, 'disabled'> & {
  disabled?: boolean
  href?: string
  target?: string
  prefetch?: boolean
}
/**
 * Button
 *
 * Re-exporting the Chakra Button to allow the disabled prop
 */
export const Button = forwardRef<ButtonProps, 'button'>(({ isDisabled, href, disabled, ...props }, ref) => {
  return <ChakraButton {...asLink(href)} ref={ref} isDisabled={disabled || isDisabled} {...props} />
})

type CustomIconButtonProps = {
  showTooltip?: boolean
  tooltipProps?: TooltipProps
} & IconButtonProps

/**
 * Icon Button
 *
 * Custom Icon Button based on Chakra's Icon Button with an optional tooltip display. To display a tooltip with the
 * icon button set `showTooltip` and pass in any tooltipProps needed.
 *
 * See: https://v1.chakra-ui.com/docs/components/form/icon-button
 */
export const IconButton = forwardRef(({ showTooltip = false, tooltipProps, ...props }: CustomIconButtonProps, ref) => {
  const variantStyles = {
    primary: { color: 'var(--vndly-color-container-light)' },
    deletePrimary: { color: 'var(--vndly-color-container-light)' },
    deleteSecondary: { color: 'var(--vndly-color-button-delete-primary)' }
  }

  const button = <ChakraIconButton sx={variantStyles} ref={ref} {...props} />

  if (showTooltip) {
    return (
      <Tooltip label={props['aria-label']} {...tooltipProps}>
        <chakra.span display={props.display}>{button}</chakra.span>
      </Tooltip>
    )
  }

  return button
})

IconButton.displayName = 'IconButton'

export const Heading = forwardRef((props: HeadingProps, ref) => {
  const { textStyle } = props

  // When one of the heading textStyle props are set on a Heading component, use a
  // Box component instead to make sure the custom text style we have defined in our
  // theme is applied.
  if (textStyle === 'heading1') {
    return <Box as="h1" ref={ref} className="chakra-heading" {...props} />
  }
  if (textStyle === 'heading2') {
    return <Box as="h2" ref={ref} className="chakra-heading" {...props} />
  }
  if (textStyle === 'heading3' || textStyle === 'heading3Semi') {
    return <Box as="h3" ref={ref} className="chakra-heading" {...props} />
  }
  if (textStyle === 'sectionHeading') {
    return <Box as="h4" ref={ref} className="chakra-heading" {...props} />
  }

  // When any other textStyle prop is set on a Heading component, use the Box component to apply
  // that given text style, otherwise use the Chakra Heading component as it is
  return textStyle ? <Box ref={ref} className="chakra-heading" {...props} /> : <ChakraHeading ref={ref} {...props} />
})

// Use opacity for better css perf
const mutedFade = keyframes`
  from {
    opacity: .25
  }
  to {
    opacity: 1
  }
`
export const Skeleton = forwardRef<SkeletonProps, 'div'>((props, ref) => {
  return <ChakraSkeleton ref={ref} animation={`0.8s ease-out infinite alternate ${mutedFade}`} {...props} />
})
